import { Injectable, WritableSignal, computed, signal } from '@angular/core';
import {
  addWeeks,
  startOfWeek,
  Day,
  addMonths,
  endOfWeek,
  addDays,
  startOfDay,
  isSameMonth,
  isSameDay,
  startOfMonth,
  format,
  endOfDay,
} from 'date-fns';
import { UTCDate } from '@date-fns/utc';
import { WorkspaceService } from './workspace.service';
import { Doddle } from '../models';
import { PostService } from './post.service';
import { EPostState } from '../enums';

export type CalendarDay = {
  utcDate: Date;
  plainDate: string;
  isPastDay: boolean;
  isToday: boolean;
  isFirstDayOfMonth: boolean;
  isSameMonthAsReferenceDate: boolean;
  doddles: Doddle[];
};

export enum CalendarView {
  Week = 'week',
  Month = 'month',
}

const plainDateFormat = 'yyyy-MM-dd';

const DATELESS = 'dateless';

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  draggedPost = signal<Doddle | null>(null);

  constructor(
    private workspaceService: WorkspaceService,
    private postService: PostService,
  ) {}

  datelessDoddles = computed(() => this.postService.datelessDoddles());

  /**
   * The reference date that is used to view days
   */
  referenceDate: WritableSignal<Date> = signal(this.defaultMonthReferenceDate);

  /**
   * The day that the week starts on
   * 0 is Sunday, 1 is Monday, 2 is Tuesday, etc.
   */
  weekStartsOn: WritableSignal<Day> = signal(1);

  /**
   * The reference date that is used to view days
   */
  protected view: WritableSignal<CalendarView> = signal(CalendarView.Month);

  private doddles = computed(() =>
    this.doddlesToMapByDate(this.postService.allDoddles()),
  );

  /**
   * Returns true if the calendar is in month view
   */
  isMonthView = computed(() => this.view() === CalendarView.Month);

  /**
   * First day that should be displayed in the calendar
   */
  startDate = computed(() =>
    this.isMonthView()
      ? startOfWeek(new UTCDate(this.referenceDate()), {
          weekStartsOn: this.weekStartsOn(),
        })
      : startOfWeek(new UTCDate(this.referenceDate()), {
          weekStartsOn: this.weekStartsOn(),
        }),
  );

  /**
   * Last day that should be displayed in the calendar
   */
  endDate = computed(() =>
    this.isMonthView()
      ? endOfWeek(addMonths(this.referenceDate(), 1), {
          weekStartsOn: this.weekStartsOn(),
        })
      : endOfDay(addDays(this.startDate(), 6)),
  );

  /**
   * Returns array of the days that should be displayed in the calendar
   */
  days = computed(() => {
    const days = [];
    const startOfToday = startOfDay(new UTCDate());
    const doddles = this.doddles();
    let currentDay = this.startDate();

    while (currentDay <= this.endDate()) {
      const plainDate = format(currentDay, plainDateFormat);
      const day: CalendarDay = {
        utcDate: currentDay,
        plainDate,
        isPastDay: currentDay < startOfToday,
        isToday: isSameDay(currentDay, startOfToday),
        isFirstDayOfMonth: currentDay.getDate() === 1,
        isSameMonthAsReferenceDate: isSameMonth(
          currentDay,
          this.referenceDate(),
        ),
        doddles: doddles.get(plainDate) || [],
      };
      days.push(day);

      currentDay = addDays(currentDay, 1);
    }

    return days;
  });

  changeView(view: CalendarView) {
    this.view.set(view);
    this.referenceDate.set(this.defaultReferenceDate);
  }

  nextCalendarPage() {
    this.isMonthView()
      ? this.changeReferenceDateByMonths(1)
      : this.changeReferenceDateByDays(7);
  }

  previousCalendarPage() {
    this.isMonthView()
      ? this.changeReferenceDateByMonths(-1)
      : this.changeReferenceDateByDays(-7);
  }

  goToTodaysCalendarPage() {
    this.referenceDate.set(this.defaultReferenceDate);
    this.loadDoddlesForCurrentDateRange();
  }

  loadDoddlesForCurrentDateRange() {
    if (!this.workspaceService.activeWorkspace().id) return;
    this.postService.getWorkspaceDoddlesForDateRange(
      this.workspaceService.activeWorkspace().id,
      this.startDate().toISOString(),
      this.endDate().toISOString(),
    );
  }

  /**
   * Handles the drop event for a calendar day.
   *
   * This function is responsible for updating the publish date of a dragged post
   * when it is dropped onto a calendar day. If the dragged post is moved between
   * calendar cells or from a list of lonely doddles to the calendar, the publish
   * date is updated to match the drop date with the same time as the original publish date.
   *
   * If the new publish date is in the past, the update is not performed.
   *
   * @param day - The calendar day where the post is dropped. If null, doddle will be moved to lonely doddles.
   */
  drop(day: CalendarDay | null) {
    if (!this.draggedPost()) return;

    let dropDate: Date | null = null;

    if (day) {
      const now = new Date();

      const doddlePlainDate = format(
        this.draggedPost()?.publishAt ?? now,
        plainDateFormat,
      );

      const publishAtDate = this.draggedPost()!.publishAt
        ? new Date(this.draggedPost()!.publishAt!)
        : now;

      if (doddlePlainDate && day.plainDate === doddlePlainDate) {
        return;
      }

      dropDate = new Date(day.plainDate);

      dropDate.setHours(
        publishAtDate.getHours(),
        publishAtDate.getMinutes(),
        publishAtDate.getSeconds(),
      );
    }

    const newPublishDate = dropDate ? dropDate.toISOString() : null;

    if (newPublishDate && newPublishDate < new Date().toISOString()) return; // If drop date is in the past, do not update the doddle

    if (newPublishDate) {
      this.updateStoredPostDate(newPublishDate);
    } else {
      // Handle case when doddle is moved to dateless (no publish date)
      this.updateStoredPostDateToNull();
    }

    this.postService
      .updateDoddle(
        this.draggedPost()!.id,
        {
          publishAt: newPublishDate,
          ...(dropDate === null && { status: EPostState.draft }),
        },
        true,
      )
      .subscribe(() => {
        this.draggedPost.set(null);
      });
  }

  updateStoredPostDate(newPublishDate: string) {
    // Remove from datelessDoddles if it was there
    this.postService.datelessDoddles.update((datelessDoddles) => {
      return datelessDoddles.filter(
        (item) => item.id !== this.draggedPost()!.id,
      );
    });

    // Update or add to doddles (dated posts)
    this.postService.doddles.update((doddles) => {
      const doddle = doddles.find((d) => d.id === this.draggedPost()!.id);
      if (doddle) {
        doddle.publishAt = newPublishDate;
        return doddles.map((item) =>
          item.id === this.draggedPost()!.id ? doddle : item,
        );
      } else {
        // If not in doddles, add it
        const doddleToAdd = {
          ...this.draggedPost()!,
          publishAt: newPublishDate,
        };
        return [...doddles, doddleToAdd];
      }
    });
  }

  updateStoredPostDateToNull() {
    // Remove from doddles (dated posts)
    this.postService.doddles.update((doddles) => {
      return doddles.filter((item) => item.id !== this.draggedPost()!.id);
    });

    // Add to datelessDoddles if not already there
    this.postService.datelessDoddles.update((datelessDoddles) => {
      const existingDoddle = datelessDoddles.find(
        (item) => item.id === this.draggedPost()!.id,
      );
      if (!existingDoddle) {
        const doddleToMove = { ...this.draggedPost()!, publishAt: null };
        return [...datelessDoddles, doddleToMove];
      }
      return datelessDoddles;
    });
  }

  private get defaultReferenceDate(): Date {
    return this.isMonthView()
      ? this.defaultMonthReferenceDate
      : this.defaultWeekReferenceDate;
  }

  private get defaultMonthReferenceDate(): Date {
    return addWeeks(new UTCDate(), -1);
  }

  private get defaultWeekReferenceDate(): Date {
    return new UTCDate();
  }

  private changeReferenceDateByDays(days: number) {
    this.referenceDate.set(addDays(this.referenceDate(), days));
    this.loadDoddlesForCurrentDateRange();
  }

  private changeReferenceDateByMonths(months: number) {
    this.referenceDate.set(
      addMonths(startOfMonth(this.referenceDate()), months),
    );
    this.loadDoddlesForCurrentDateRange();
  }

  /**
   * Maps doddles to a Map object where key is a YYYY-MM-DD date value and value is an array of doddles
   */
  private doddlesToMapByDate(doddles: Doddle[]): Map<string, Doddle[]> {
    const doddleMap = new Map();
    doddles.forEach((doddle) => {
      const date = doddle?.publishAt
        ? format(doddle.publishAt, plainDateFormat)
        : DATELESS;

      if (!doddleMap.has(date)) {
        doddleMap.set(date, [doddle]);
      } else {
        doddleMap.get(date).push(doddle);
      }
    });

    return doddleMap;
  }
}
